import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { ReactElement, useState } from 'react';
import { Redirect } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { Auth } from '../../../routes/config/Auth';
import { NotificationLevel, ToastNotification } from '../../notifications/models/notification.model';
import { INotificationsService } from '../../notifications/services/notifications.service';
import { useAuthService } from '../auth.service';
import { IAuthStore } from '../auth.store';
import { ResetPasswordForm } from '../components/ResetPasswordForm';

export const ResetPasswordContainer = (): ReactElement => {
  const authService = useAuthService();
  const authStore = useInjection(IAuthStore.$);
  const notificationService = useInjection(INotificationsService.$);

  const [password, setPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState(InputStatus.empty());
  const [externalStatus, setExternalStatus] = useState(InputStatus.empty());
  const [isRequestPending, setIsRequestPending] = useState(false);
  const { token } = useParams<{ token: string }>();
  const history = useHistory();

  const handlePasswordChange = (value: string): void => {
    setPassword(value);
  };

  const handlePasswordStatusChange = (status: InputStatus): void => {
    setPasswordStatus(status);
  };

  const handleResetPassword = async (): Promise<void> => {
    if (!passwordStatus.isValid) {
      return;
    }

    setIsRequestPending(true);

    const result = await authService.resetPassword(token, password);

    if (result instanceof ACDLError) {
      const errorCodes: TranslationKey[] = result.apiErrorResponse ? result.apiErrorResponse.errorCodes : ['something_went_wrong'];

      setExternalStatus(new InputStatus(false, errorCodes));
      setIsRequestPending(false);
      return;
    }

    await notificationService.push(new ToastNotification(NotificationLevel.SUCCESS, 'your_password_reset_succesfully'));

    history.replace(Auth.Login.Path);
  };

  if (authStore.isAuthenticated) {
    return <Redirect to={authService.getAndClearReferrer()} />;
  }

  return (
    <ResetPasswordForm
      onPasswordChange={handlePasswordChange}
      onPasswordStatusChange={handlePasswordStatusChange}
      onResetPassword={handleResetPassword}
      passwordStatus={passwordStatus}
      externalStatus={externalStatus}
      isDisabledSubmit={isRequestPending}
    />
  );
};
